import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Languages } from "lucide-react";
import { LOCALES, LOCALE_NAMES, type Locale } from "@rec/shared";
import { setLocale, useLocale } from "@/i18n";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * Pick the interface language.
 *
 * Each language is named in itself ("Français", not "French"): someone who
 * cannot read the current language must still be able to find their own.
 * The choice is applied at once, kept on this device, and saved to the
 * account by `useLocaleSync` — see `@/i18n`.
 *
 * `menu` is the compact header control; `select` is the labelled form field
 * for the account page and the signed-out screens.
 */
export function LanguageSwitcher({ variant = "menu" }: { variant?: "menu" | "select" }) {
  const { t } = useTranslation();
  const locale = useLocale();

  if (variant === "select") {
    return (
      <label className="flex items-center gap-2 text-[13px] text-text-muted">
        <Languages size={14} aria-hidden="true" />
        <span className="sr-only">{t("language.label")}</span>
        <select
          value={locale}
          onChange={(e) => void setLocale(e.target.value as Locale)}
          className="h-9 rounded-lg border border-border bg-bg px-2 text-[13px] text-text focus:border-accent focus:outline-none"
          aria-label={t("language.label")}
        >
          {LOCALES.map((code) => (
            <option key={code} value={code} lang={code}>
              {LOCALE_NAMES[code]}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return <LanguageMenu />;
}

function LanguageMenu() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        title={t("language.choose")}
        aria-label={t("language.choose")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* The code, not just an icon: the current language is visible at a
            glance, and "FR" is recognisable to someone who reads neither. */}
        <span className="text-[11px] font-semibold uppercase tracking-wide">{locale}</span>
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[150px] rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-md)]"
        >
          {LOCALES.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === locale}
              lang={code}
              onClick={() => {
                void setLocale(code);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-md px-2.5 py-1.5 text-left text-[13px] text-text hover:bg-surface-hover",
                code === locale && "font-medium",
              )}
            >
              {LOCALE_NAMES[code]}
              {code === locale ? <Check size={13} className="text-accent" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
